import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDeleteManualDividend } from "../../hooks/dividends";
import type { CalendarEntry, DividendSource } from "../../lib/dividends";
import type { MonthTileData } from "../../lib/dividends-utils";

interface Props {
  data: MonthTileData;
  onClose: () => void;
  onAddManual: (month: string) => void;
}

function fmtEur(cents: number | undefined): string {
  if (cents === undefined) return "—";
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(cents / 100);
}

function fmtPerShare(cents: number | undefined, currency: string | undefined): string {
  if (cents === undefined) return "—";
  const value = cents / 100;
  return `${value.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ${currency ?? ""}`.trim();
}

function monthLabel(month: string): string {
  const [year, mm] = month.split("-");
  const names = [
    "Januar",
    "Februar",
    "März",
    "April",
    "Mai",
    "Juni",
    "Juli",
    "August",
    "September",
    "Oktober",
    "November",
    "Dezember",
  ];
  const idx = Number.parseInt(mm ?? "1", 10) - 1;
  return `${names[idx] ?? "?"} ${year}`;
}

function statusForEntry(e: CalendarEntry): "PAID" | "ANNOUNCED" | "FORECAST" {
  if (e.kind === "PAST") return "PAID";
  if (e.source === "EXTRAPOLATED") return "FORECAST";
  return "ANNOUNCED";
}

export function MonthDetailModal({ data, onClose, onAddManual }: Props) {
  const { t } = useTranslation();
  const deleteManual = useDeleteManualDividend();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const sorted = [...data.entries].sort((a, b) => {
    const c = a.payDate.localeCompare(b.payDate);
    return c !== 0 ? c : a.investmentName.localeCompare(b.investmentName);
  });

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: ESC-Handler übernimmt Keyboard-Close (document listener)
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation-Guard, kein Keyboard-Äquivalent nötig */}
      <div
        className="rounded-xl border max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        style={{ background: "var(--card)", borderColor: "var(--border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{ borderColor: "var(--border)" }}
        >
          <div>
            <h2 className="text-lg font-bold">
              {t("dividends.modal.title", { monthLabel: monthLabel(data.month) })}
            </h2>
            <div className="text-sm mt-0.5" style={{ color: "var(--muted)" }}>
              {t("dividends.modal.sumLabel", { sum: fmtEur(data.totalEur) })}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 rounded-lg text-sm"
            style={{ color: "var(--muted)" }}
            aria-label={t("dividends.modal.close")}
          >
            ✕
          </button>
        </div>

        <div className="overflow-auto flex-1 p-4">
          <table className="w-full text-sm">
            <thead>
              <tr
                className="text-left border-b"
                style={{ borderColor: "var(--border)", color: "var(--muted)" }}
              >
                <th className="py-2 pr-3">{t("dividends.modal.columnPosition")}</th>
                <th className="py-2 pr-3">{t("dividends.modal.columnExDate")}</th>
                <th className="py-2 pr-3">{t("dividends.modal.columnPayDate")}</th>
                <th className="py-2 pr-3 text-right">{t("dividends.modal.columnShares")}</th>
                <th className="py-2 pr-3 text-right">{t("dividends.modal.columnPerShare")}</th>
                <th className="py-2 pr-3 text-right">{t("dividends.modal.columnTotal")}</th>
                <th className="py-2 pr-3">{t("dividends.modal.columnStatus")}</th>
                <th className="py-2 pr-3">{t("dividends.modal.columnSource")}</th>
                <th className="py-2 pr-3">{t("dividends.modal.columnActions")}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((e) => (
                <tr key={e.id} className="border-b" style={{ borderColor: "var(--border)" }}>
                  <td className="py-2 pr-3">
                    <div className="font-semibold">{e.investmentName}</div>
                    <div className="text-xs" style={{ color: "var(--muted)" }}>
                      {e.symbol}
                    </div>
                  </td>
                  <td className="py-2 pr-3">{e.exDate ?? "—"}</td>
                  <td className="py-2 pr-3">{e.payDate}</td>
                  <td className="py-2 pr-3 text-right">
                    {e.sharesAtPayDate ?? e.currentShares ?? "—"}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    {fmtPerShare(e.amountPerShareCents, e.currency)}
                  </td>
                  <td className="py-2 pr-3 text-right font-semibold">
                    {e.kind === "PAST" ? fmtEur(e.actualTotalEur) : fmtEur(e.estimatedTotalEur)}
                  </td>
                  <td className="py-2 pr-3">
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                      style={{ color: "var(--muted)", background: "var(--card)" }}
                    >
                      {t(`dividends.status.${statusForEntry(e)}`)}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-xs" style={{ color: "var(--muted)" }}>
                    {t(
                      `dividends.sourceLabel.${(e.source ?? "YAHOO_ANNOUNCED") as DividendSource}`,
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    {e.source === "MANUAL" && (
                      <button
                        type="button"
                        onClick={() => deleteManual.mutate(e.id)}
                        disabled={deleteManual.isPending}
                        className="text-xs underline"
                        style={{ color: "var(--neg)" }}
                      >
                        {t("dividends.modal.actionDelete")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t flex justify-between" style={{ borderColor: "var(--border)" }}>
          <button
            type="button"
            onClick={() => onAddManual(data.month)}
            className="px-3 py-2 rounded-lg text-sm font-medium border"
            style={{
              borderColor: "var(--accent)",
              color: "var(--accent)",
              background: "var(--card)",
            }}
          >
            {t("dividends.modal.addManualForMonth", { monthLabel: monthLabel(data.month) })}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-lg text-sm font-medium border"
            style={{ borderColor: "var(--border)", background: "var(--card)" }}
          >
            {t("dividends.modal.close")}
          </button>
        </div>
      </div>
    </div>
  );
}
